/**
 * 监控告警浏览器 TTS：设置读写、文案拼装、串行播报队列。
 * 每条：先播放提醒音 mp3，再 speechSynthesis 念白。
 * 触发门控（仅 SSE 新告警）由 List 负责。
 * 无婴儿：念完后隔设定秒数再把同一条放回队尾；离开无婴儿活跃集后停止。
 *
 * 长期开页防护：队列上限、单条超时、cancel/reset 清理；页面隐藏由 List 调 cancel。
 */

import {
  getEventTypeLabel,
  getMonitoringAlertSummary,
  getMonitoringSceneFlags,
  getSeeData
} from '@/views/commandCenter/profile/ServiceAlerts/monitoringSsePayloadUtils'

export const ALERT_TTS_SETTINGS_STORAGE_KEY = 'surveillance.alertTts.settings'

export const DEFAULT_ALERT_TTS_SETTINGS = {
  enabled: true,
  rate: 1,
  pitch: 1,
  volume: 1,
  /** 无婴儿念完后隔一段时间再入队 */
  repeatNoBaby: true,
  /** 无婴儿重复间隔（秒） */
  repeatIntervalSec: 60
}

/** 待播队列上限；超出丢弃最旧，避免洪峰/卡死时无限堆积 */
const MAX_SPEAK_QUEUE = 40
/** 单条播报超时（ms）；含提醒音 + 念白；onend 丢失时强制推进队列 */
const UTTERANCE_TIMEOUT_MS = 20000
/** public/ 下提醒音；播放失败则跳过直接念白 */
const ALERT_CHIME_SRC = `${process.env.PUBLIC_URL || ''}/4204.mp3`
/** 提醒音相对念白的体感增益（同 volume 设置下叮咚明显偏响，压低以对齐听感） */
const CHIME_RELATIVE_GAIN = 0.28
/** 提醒音结束后再稍停再念白 */
const CHIME_TO_SPEECH_GAP_MS = 120
/** 单条提醒音最长等待，避免 ended 丢失卡住队列 */
const CHIME_PLAY_TIMEOUT_MS = 8000

const spokenStreamKeys = new Set()
const speakQueue = []
/** 仍要重复的无婴儿：streamKey → { text, streamKey, repeat } */
const armedNoBabyRepeats = new Map()
/** streamKey → 等待再次入队的 timer */
const noBabyRepeatTimers = new Map()
/** List 同步的当前无婴儿路；null 表示还没同步 */
let noBabyRepeatableKeys = null
/** 正在念的队列项，念完后决定要不要再排 */
let speakingItem = null
let speaking = false
let audioUnlocked = false
let pendingAfterUnlock = false
/** 当前 utterance 世代；cancel/超时后递增，避免 onend/onerror 双触发 */
let speakGeneration = 0
let speakTimeoutId = 0
/** 当前提醒音 Audio；cancel 时 pause 打断 */
let chimeAudio = null

function clamp (n, min, max, fallback) {
  const v = Number(n)
  if (!Number.isFinite(v)) return fallback
  if (v < min) return min
  if (v > max) return max
  return v
}

function clearSpeakTimeout () {
  if (!speakTimeoutId) return
  if (typeof window !== 'undefined') window.clearTimeout(speakTimeoutId)
  speakTimeoutId = 0
}

function stopAlertChime () {
  if (!chimeAudio) return
  try {
    chimeAudio.onended = null
    chimeAudio.onerror = null
    chimeAudio.pause()
    chimeAudio.removeAttribute('src')
    chimeAudio.load()
  } catch (e) {
    // ignore
  }
  chimeAudio = null
}

function invalidateSpeakCallbacks () {
  speakGeneration += 1
  clearSpeakTimeout()
  stopAlertChime()
}

/**
 * 播放提醒音 mp3；失败/不支持时直接 resolve，不阻塞念白。
 * @param {number} volume 设置页音量 0~1（念白用原值；叮咚乘 CHIME_RELATIVE_GAIN）
 * @param {number} gen 世代；cancel 后勿再继续
 * @returns {Promise<void>}
 */
function playAlertChime (volume, gen) {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || typeof Audio === 'undefined') {
      resolve()
      return
    }
    if (gen !== speakGeneration) {
      resolve()
      return
    }

    let settled = false
    let gapTimerId = 0
    let guardTimerId = 0
    const done = () => {
      if (settled) return
      settled = true
      if (gapTimerId) window.clearTimeout(gapTimerId)
      if (guardTimerId) window.clearTimeout(guardTimerId)
      stopAlertChime()
      resolve()
    }

    try {
      stopAlertChime()
      const audio = new Audio(ALERT_CHIME_SRC)
      chimeAudio = audio
      const baseVolume = clamp(volume, 0, 1, 1)
      // 叮咚素材峰值远高于 TTS，按相对增益压低后再跟设置音量联动
      audio.volume = clamp(baseVolume * CHIME_RELATIVE_GAIN, 0, 1, 0)
      audio.preload = 'auto'

      const finishAfterGap = () => {
        if (gen !== speakGeneration) {
          done()
          return
        }
        gapTimerId = window.setTimeout(() => {
          gapTimerId = 0
          done()
        }, CHIME_TO_SPEECH_GAP_MS)
      }

      audio.onended = finishAfterGap
      audio.onerror = () => done()
      guardTimerId = window.setTimeout(() => {
        guardTimerId = 0
        done()
      }, CHIME_PLAY_TIMEOUT_MS)

      const playRet = audio.play()
      if (playRet && typeof playRet.then === 'function') {
        playRet.catch(() => done())
      }
    } catch (e) {
      done()
    }
  })
}

function normalizeAlertTtsSettings (raw) {
  const src = raw && typeof raw === 'object' ? raw : {}
  return {
    enabled: src.enabled !== false,
    rate: clamp(src.rate, 0.5, 2, DEFAULT_ALERT_TTS_SETTINGS.rate),
    pitch: clamp(src.pitch, 0, 2, DEFAULT_ALERT_TTS_SETTINGS.pitch),
    volume: clamp(src.volume, 0, 1, DEFAULT_ALERT_TTS_SETTINGS.volume),
    repeatNoBaby: src.repeatNoBaby !== false,
    repeatIntervalSec: clamp(
      src.repeatIntervalSec,
      10,
      600,
      DEFAULT_ALERT_TTS_SETTINGS.repeatIntervalSec
    )
  }
}

export function loadAlertTtsSettings () {
  try {
    const raw = localStorage.getItem(ALERT_TTS_SETTINGS_STORAGE_KEY)
    if (!raw) return { ...DEFAULT_ALERT_TTS_SETTINGS }
    return normalizeAlertTtsSettings(JSON.parse(raw))
  } catch (e) {
    return { ...DEFAULT_ALERT_TTS_SETTINGS }
  }
}

export function saveAlertTtsSettings (next) {
  const settings = normalizeAlertTtsSettings(next)
  localStorage.setItem(ALERT_TTS_SETTINGS_STORAGE_KEY, JSON.stringify(settings))
  return settings
}

/**
 * 播报文案位置：设备行 bedNo（Java 字段）；缺省回退通道号。口语「X号床」。
 */
export function resolveSurveillanceLocationLabel (seeData, deviceRow) {
  const bedNo = String(deviceRow?.bedNo ?? '').trim()
  if (bedNo) return `${bedNo}号床`
  const channelNo = String(deviceRow?.channelNo ?? seeData?.channelNo ?? '').trim()
  if (!channelNo) return '未知床位'
  return `${channelNo}号床`
}

export function resolveSurveillanceAlertLabel (seeData) {
  if (!seeData || typeof seeData !== 'object') return '监控异常'
  const typeLabel = getEventTypeLabel(seeData)
  const summary = getMonitoringAlertSummary(seeData)
  const candidate = typeLabel || summary || seeData.subTypeDesc || seeData.alarmName || ''
  const text = String(candidate).trim()
  return text || '监控异常'
}

export function buildAlertSpeakText ({ locationLabel, alertLabel }) {
  const loc = locationLabel != null && String(locationLabel).trim() !== ''
    ? String(locationLabel).trim()
    : '未知床位'
  const alert = alertLabel != null && String(alertLabel).trim() !== ''
    ? String(alertLabel).trim()
    : '监控异常'
  return `${loc}，${alert}`
}

export function hasSpokenAlertStreamKey (streamKey) {
  return Boolean(streamKey) && spokenStreamKeys.has(streamKey)
}

export function markSpokenAlertStreamKey (streamKey) {
  if (!streamKey) return
  spokenStreamKeys.add(streamKey)
}

export function clearSpokenAlertStreamKey (streamKey) {
  if (!streamKey) return
  spokenStreamKeys.delete(streamKey)
}

/** 清除不在活跃集合中的已播记录（处理后可再次播报） */
export function pruneSpokenAlertStreamKeys (activeStreamKeys) {
  const active = activeStreamKeys instanceof Set
    ? activeStreamKeys
    : new Set(Array.isArray(activeStreamKeys) ? activeStreamKeys : [])
  spokenStreamKeys.forEach((sk) => {
    if (!active.has(sk)) spokenStreamKeys.delete(sk)
  })
}

export function isAlertTtsAudioUnlocked () {
  return audioUnlocked
}

export function markAlertTtsAudioUnlocked () {
  if (audioUnlocked) return
  audioUnlocked = true
  if (pendingAfterUnlock) {
    pendingAfterUnlock = false
    drainSpeakQueue()
  }
}

function clearNoBabyRepeatTimer (streamKey) {
  const timerId = noBabyRepeatTimers.get(streamKey)
  if (!timerId) return
  window.clearTimeout(timerId)
  noBabyRepeatTimers.delete(streamKey)
}

function clearAllNoBabyRepeatTimers () {
  noBabyRepeatTimers.forEach((timerId) => {
    window.clearTimeout(timerId)
  })
  noBabyRepeatTimers.clear()
}

function pushSpeakItem (item) {
  if (item && item.repeat === true && item.streamKey) {
    for (let i = speakQueue.length - 1; i >= 0; i -= 1) {
      const queued = speakQueue[i]
      if (queued && queued.repeat === true && queued.streamKey === item.streamKey) {
        speakQueue.splice(i, 1)
      }
    }
  }
  speakQueue.push(item)
  while (speakQueue.length > MAX_SPEAK_QUEUE) {
    speakQueue.shift()
  }
  drainSpeakQueue()
}

/** 念完后隔设置的秒数，把同一句再放回队尾。间隔只耽误这一条，不卡住其它告警。 */
function scheduleNoBabyRepeat (item) {
  if (!item || item.repeat !== true || !item.streamKey) return
  const streamKey = item.streamKey
  if (noBabyRepeatableKeys && !noBabyRepeatableKeys.has(streamKey)) return
  const settings = loadAlertTtsSettings()
  if (!settings.enabled || settings.repeatNoBaby !== true) return
  if (typeof document !== 'undefined' && document.hidden) return
  clearNoBabyRepeatTimer(streamKey)
  const waitMs = Math.round(settings.repeatIntervalSec * 1000)
  const text = item.text
  const timerId = window.setTimeout(() => {
    noBabyRepeatTimers.delete(streamKey)
    const latest = loadAlertTtsSettings()
    if (!latest.enabled || latest.repeatNoBaby !== true) return
    if (typeof document !== 'undefined' && document.hidden) return
    if (noBabyRepeatableKeys && !noBabyRepeatableKeys.has(streamKey)) return
    const speakingSame = speakingItem
      && speakingItem.repeat === true
      && speakingItem.streamKey === streamKey
    if (speakingSame) return
    pushSpeakItem({
      text,
      settings: latest,
      streamKey,
      repeat: true
    })
  }, waitMs)
  noBabyRepeatTimers.set(streamKey, timerId)
}

/** 监控墙交来仍是无婴儿的路。不在集合里的重复停掉。 */
export function syncNoBabyRepeatStreamKeys (activeStreamKeys) {
  const active = activeStreamKeys instanceof Set
    ? activeStreamKeys
    : new Set(Array.isArray(activeStreamKeys) ? activeStreamKeys : [])
  noBabyRepeatableKeys = active
  Array.from(armedNoBabyRepeats.keys()).forEach((streamKey) => {
    if (active.has(streamKey)) return
    armedNoBabyRepeats.delete(streamKey)
    clearNoBabyRepeatTimer(streamKey)
  })
  for (let i = speakQueue.length - 1; i >= 0; i -= 1) {
    const queued = speakQueue[i]
    if (!queued || queued.repeat !== true || !queued.streamKey) continue
    if (active.has(queued.streamKey)) continue
    speakQueue.splice(i, 1)
  }
}

/** 回到页面后，仍在无婴儿的路按间隔再排，不立刻补念。 */
export function resumeNoBabyAlertRepeats () {
  if (typeof document !== 'undefined' && document.hidden) return
  armedNoBabyRepeats.forEach((item) => {
    if (noBabyRepeatTimers.has(item.streamKey)) return
    scheduleNoBabyRepeat(item)
  })
}

/** 停播并清空队列和等待中的再次入队；保留已播记录，以及仍要重复的无婴儿登记 */
export function cancelSurveillanceAlertSpeak () {
  invalidateSpeakCallbacks()
  clearAllNoBabyRepeatTimers()
  speakQueue.length = 0
  speaking = false
  speakingItem = null
  pendingAfterUnlock = false
  try {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel()
    }
  } catch (e) {
    // ignore
  }
}

function startUtterance (text, settings, gen) {
  if (gen !== speakGeneration) return
  if (typeof window === 'undefined' || !window.speechSynthesis) {
    finishSpeaking(gen)
    return
  }

  const utter = new SpeechSynthesisUtterance(text)
  utter.rate = settings.rate
  utter.pitch = settings.pitch
  utter.volume = settings.volume
  utter.onend = () => finishSpeaking(gen)
  utter.onerror = () => finishSpeaking(gen)

  try {
    window.speechSynthesis.speak(utter)
  } catch (e) {
    finishSpeaking(gen)
  }
}

/** 页面卸载时：停播 + 清空已播记录和重复登记 */
export function resetAlertTtsRuntimeState () {
  cancelSurveillanceAlertSpeak()
  spokenStreamKeys.clear()
  armedNoBabyRepeats.clear()
  noBabyRepeatableKeys = null
}

function finishSpeaking (gen) {
  if (gen !== speakGeneration) return
  clearSpeakTimeout()
  const finished = speakingItem
  speakingItem = null
  speaking = false
  scheduleNoBabyRepeat(finished)
  drainSpeakQueue()
}

function drainSpeakQueue () {
  if (speaking) return
  if (!audioUnlocked) {
    if (speakQueue.length > 0) pendingAfterUnlock = true
    return
  }
  if (typeof window === 'undefined' || !window.speechSynthesis) {
    speakQueue.length = 0
    return
  }
  while (speakQueue.length > 0) {
    const peek = speakQueue[0]
    const dropped = peek
      && peek.repeat === true
      && peek.streamKey
      && noBabyRepeatableKeys
      && !noBabyRepeatableKeys.has(peek.streamKey)
    if (!dropped) break
    speakQueue.shift()
  }
  const next = speakQueue.shift()
  if (!next) return

  const text = typeof next === 'string' ? next : next.text
  const settings = (next && typeof next === 'object' && next.settings)
    ? next.settings
    : loadAlertTtsSettings()
  if (!settings.enabled) {
    speakQueue.length = 0
    clearAllNoBabyRepeatTimers()
    speakingItem = null
    return
  }
  if (!text) {
    speaking = false
    drainSpeakQueue()
    return
  }

  speakingItem = (next && typeof next === 'object') ? next : null
  speaking = true
  const gen = speakGeneration + 1
  speakGeneration = gen

  clearSpeakTimeout()
  speakTimeoutId = window.setTimeout(() => {
    speakTimeoutId = 0
    if (gen !== speakGeneration) return
    stopAlertChime()
    try {
      window.speechSynthesis.cancel()
    } catch (e) {
      // ignore
    }
    const finished = speakingItem
    speakingItem = null
    // 递增世代，避免叮咚结束后仍 startUtterance
    speakGeneration += 1
    speaking = false
    scheduleNoBabyRepeat(finished)
    drainSpeakQueue()
  }, UTTERANCE_TIMEOUT_MS)

  // 先叮咚再念白；叮咚失败/不支持时直接念白
  playAlertChime(settings.volume, gen).then(() => {
    startUtterance(text, settings, gen)
  })
}

/**
 * @param {{ streamKey: string, seeData?: object, deviceRow?: object, text?: string }} payload
 */
export function enqueueSurveillanceAlertSpeak (payload) {
  const settings = loadAlertTtsSettings()
  if (!settings.enabled) return
  const streamKey = payload?.streamKey
  if (!streamKey) return
  if (hasSpokenAlertStreamKey(streamKey)) return

  const seeData = payload.seeData || getSeeData(payload.item) || {}
  const text = payload.text
    || buildAlertSpeakText({
      locationLabel: resolveSurveillanceLocationLabel(seeData, payload.deviceRow),
      alertLabel: resolveSurveillanceAlertLabel(seeData)
    })
  const repeat = getMonitoringSceneFlags(seeData).hasBaby === false && settings.repeatNoBaby === true
  const item = { text, settings, streamKey, repeat }

  markSpokenAlertStreamKey(streamKey)
  if (repeat) armedNoBabyRepeats.set(streamKey, { text, streamKey, repeat: true })
  pushSpeakItem(item)
}

/**
 * 设置页试听：叮咚 + 示例念白。用当前表单参数，不落盘、不依赖总开关。
 * @param {{ rate?: number, pitch?: number, volume?: number, text?: string }} [partial]
 */
export function previewSurveillanceAlertSpeak (partial = {}) {
  markAlertTtsAudioUnlocked()
  const base = loadAlertTtsSettings()
  const settings = {
    enabled: true,
    rate: clamp(partial.rate, 0.5, 2, base.rate),
    pitch: clamp(partial.pitch, 0, 2, base.pitch),
    volume: clamp(partial.volume, 0, 1, base.volume)
  }
  const text = partial.text != null && String(partial.text).trim() !== ''
    ? String(partial.text).trim()
    : '1号床，监控异常'

  cancelSurveillanceAlertSpeak()
  speakQueue.push({ text, settings })
  drainSpeakQueue()
  resumeNoBabyAlertRepeats()
}
